import React, { useEffect, useRef, useState, lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, TrendingUp, ShieldCheck, Quote, Play, Pause, Star, ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { heroImages, valuePillars, testimonials } from "../mock";
import PartnerMarquee from "../components/PartnerMarquee";
import useDocumentMeta from "../hooks/useDocumentMeta";
import useJsonLd, {
  organizationSchema,
  localBusinessSchema,
  websiteSchema,
} from "../hooks/useJsonLd";

// Heavy SVG map (~50KB + 37KB TopoJSON) — load only after first paint.
const InteractiveMap = lazy(() => import("../components/InteractiveMap"));

const HERO_VIDEO = "/uploads/hero-video.mp4";
const HERO_POSTER = "/uploads/hero-video-poster.webp";

function Hero() {
  const videoRef = useRef(null);
  const [videoOk, setVideoOk] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [idx, setIdx] = useState(0);

  // Photo fallback carousel only runs if the video fails to load
  useEffect(() => {
    if (videoOk) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroImages.length), 6000);
    return () => clearInterval(t);
  }, [videoOk]);

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play();
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section className="relative bg-[#4a7b9d] pt-6 pb-10 lg:pt-10 lg:pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl overflow-hidden shadow-2xl h-[420px] md:h-[520px] lg:h-[600px] bg-black">
          {videoOk ? (
            <>
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                src={HERO_VIDEO}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                fetchPriority="low"
                poster={HERO_POSTER}
                onPlay={() => setPlaying(true)}
                onPause={() => setPlaying(false)}
                onError={() => setVideoOk(false)}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/45 to-black/20" />
            </>
          ) : (
          <>
          {heroImages.map((src, i) => (
            <div
              key={src}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ease-in-out ${
                i === idx ? "opacity-100" : "opacity-0"
              }`}
            >
              <img
                src={src}
                alt=""
                className="w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                fetchPriority={i === 0 ? "high" : "low"}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/20" />
            </div>
          ))}
          </>
          )}

          <div className="relative h-full flex items-center">
            <div className="px-6 md:px-12 lg:px-16 max-w-3xl">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 backdrop-blur-sm border border-white/20 text-white/90 text-xs tracking-[0.18em] uppercase">
                <MapPin size={14} /> Growing the Midwest Since 2012
              </span>
              <h1 className="font-display text-white mt-5 text-4xl md:text-6xl lg:text-7xl leading-[1.05] drop-shadow-md">
                Get to know the team behind the territory.
              </h1>
              <p className="mt-6 text-white/85 text-lg md:text-xl max-w-xl leading-relaxed">
                For over a decade, Creative Sales Source has been quietly growing market share for the manufacturers who build the homes of the Midwest.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/about">
                  <Button className="bg-[#4a6c7b] hover:bg-[#3d5a68] text-white h-12 px-7 rounded-md shadow-lg hover:shadow-xl transition-all group">
                    Our Story
                    <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline" className="bg-white/10 backdrop-blur-sm border-white/40 text-white hover:bg-white hover:text-[#3d5a68] h-12 px-7 rounded-md">
                    Schedule a Territory Consultation
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Video control / Carousel dots */}
          {videoOk ? (
            <button
              onClick={toggle}
              aria-label={playing ? "Pause hero video" : "Play hero video"}
              className="absolute bottom-5 right-5 w-11 h-11 rounded-full bg-white/15 backdrop-blur-sm border border-white/30 text-white flex items-center justify-center hover:bg-white/25 transition-colors"
            >
              {playing ? <Pause size={18} /> : <Play size={18} />}
            </button>
          ) : (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-white" : "w-4 bg-white/50 hover:bg-white/80"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Stat strip */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px mt-8 bg-white/20 rounded-2xl overflow-hidden">
          {[
            { k: "10+", v: "Years in market" },
            { k: "9", v: "States covered" },
            { k: "10+", v: "Active distributors" },
            { k: "100%", v: "Building materials focus" },
          ].map((s) => (
            <div key={s.v} className="bg-[#4a7b9d] px-6 py-6 text-center text-white">
              <div className="font-display text-3xl md:text-4xl">{s.k}</div>
              <div className="mt-1 text-xs md:text-sm text-white/80 tracking-wide uppercase">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Advantage() {
  return (
    <section className="bg-white py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <p className="text-[#4a6c7b] uppercase text-xs tracking-[0.22em] font-semibold">
            The Creative Sales Source Advantage
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2d3e4a] mt-4 leading-[1.05]">
            More than a sales group that waits for leads.
          </h2>
          <p className="mt-5 text-slate-600 text-lg leading-relaxed">
            We're a small, senior team that treats every line we represent like our own. Here's what that looks like in practice.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-x-12 gap-y-12">
          {valuePillars.map((p) => (
            <div key={p.number} className="group relative">
              <div className="flex items-start gap-5">
                <div className="flex-shrink-0 font-display text-5xl text-[#4a7b9d]/30 group-hover:text-[#4a7b9d] transition-colors duration-300 leading-none">
                  {p.number}
                </div>
                <div>
                  <h3 className="font-display text-xl md:text-2xl text-[#2d3e4a]">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{p.body}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Partners() {
  return <PartnerMarquee />;
}

function Coverage() {
  return (
    <section className="relative bg-[#4a7b9d] py-20 lg:py-24 overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)",
        backgroundSize: "40px 40px, 60px 60px",
      }} />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-2">
            <p className="text-white/80 uppercase text-xs tracking-[0.22em] font-semibold">
              Our Coverage
            </p>
            <h2 className="font-display text-white text-4xl md:text-5xl mt-4 leading-tight">
              From Indiana through Montana.
            </h2>
            <p className="mt-5 text-white/85 text-lg leading-relaxed max-w-lg">
              9 states. A network of distributors, pro-dealers, and contractors built over three decades of in-market work.
            </p>
            <div className="mt-8 flex items-center gap-3 text-white/90">
              <TrendingUp size={20} />
              <span className="text-sm tracking-wide">Adding two new territories in 2026.</span>
            </div>
          </div>
          <div className="lg:col-span-3">
            <Suspense fallback={<div className="w-full aspect-[7/4] rounded-xl bg-white/5 animate-pulse" />}>
              <InteractiveMap />
            </Suspense>
          </div>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const [i, setI] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = testimonials.length;

  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setI((v) => (v + 1) % count), 7000);
    return () => clearInterval(t);
  }, [paused, count]);

  const t = testimonials[i];
  const prev = () => setI((v) => (v - 1 + count) % count);
  const next = () => setI((v) => (v + 1) % count);

  return (
    <section
      className="relative bg-white py-20 lg:py-28 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Soft background accent */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 15% 20%, #4a7b9d 1px, transparent 1px), radial-gradient(circle at 85% 75%, #4a6c7b 1px, transparent 1px)",
          backgroundSize: "40px 40px, 50px 50px",
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <p className="text-[#4a6c7b] uppercase text-xs tracking-[0.22em] font-semibold">
            What partners say
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2d3e4a] mt-4 leading-[1.05]">
            Trusted by the people who matter most.
          </h2>
          <p className="mt-5 text-slate-600">
            Real words from manufacturers we've represented across the Midwest.
          </p>
        </div>

        {/* Featured testimonial card */}
        <div className="relative max-w-4xl mx-auto">
          <div className="absolute -top-4 -left-2 text-[#4a7b9d]/15 hidden md:block">
            <Quote size={88} strokeWidth={1} />
          </div>

          <div
            key={i}
            className="relative bg-white rounded-3xl border border-slate-200/80 shadow-xl hover:shadow-2xl transition-shadow duration-500 p-8 md:p-12"
            style={{ animation: "tFade 700ms cubic-bezier(0.22, 1, 0.36, 1)" }}
          >
            {/* Stars */}
            <div className="flex gap-1 mb-5">
              {Array.from({ length: t.rating || 5 }).map((_, idx) => (
                <Star key={idx} size={18} className="fill-[#ffb000] text-[#ffb000]" />
              ))}
            </div>

            {/* Quote */}
            <blockquote className="font-display text-2xl md:text-3xl lg:text-[2rem] text-[#2d3e4a] leading-[1.3]">
              &ldquo;{t.quote}&rdquo;
            </blockquote>

            {/* Author */}
            <div className="mt-8 pt-6 border-t border-slate-100 flex items-center gap-4">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-gradient-to-br from-[#4a7b9d] to-[#3d5a68] text-white font-display text-lg flex items-center justify-center shadow-md">
                {t.initials}
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-[#2d3e4a] text-base">{t.author}</p>
                <p className="text-slate-500 text-sm leading-tight mt-0.5">
                  {t.title}
                  {t.company && (
                    <>
                      <span className="mx-1.5 text-slate-300">·</span>
                      <span className="text-[#4a6c7b] font-medium">{t.company}</span>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Prev / Next controls */}
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              onClick={prev}
              aria-label="Previous testimonial"
              className="w-11 h-11 rounded-full bg-white border border-slate-200 text-[#4a6c7b] hover:bg-[#4a7b9d] hover:text-white hover:border-[#4a7b9d] flex items-center justify-center shadow-sm transition-all"
            >
              <ChevronLeft size={18} />
            </button>

            <div className="flex gap-1.5 mx-2">
              {testimonials.map((_, ix) => (
                <button
                  key={ix}
                  onClick={() => setI(ix)}
                  className={`h-2 rounded-full transition-all ${
                    ix === i ? "w-8 bg-[#4a7b9d]" : "w-2 bg-slate-300 hover:bg-slate-400"
                  }`}
                  aria-label={`Testimonial ${ix + 1}`}
                />
              ))}
            </div>

            <button
              onClick={next}
              aria-label="Next testimonial"
              className="w-11 h-11 rounded-full bg-white border border-slate-200 text-[#4a6c7b] hover:bg-[#4a7b9d] hover:text-white hover:border-[#4a7b9d] flex items-center justify-center shadow-sm transition-all"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        {/* Trust strip below */}
        <div className="mt-16 pt-10 border-t border-slate-100">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="font-display text-3xl md:text-4xl text-[#4a7b9d]">14+</div>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Years building trust</p>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl text-[#4a7b9d]">9</div>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">States covered</p>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl text-[#4a7b9d]">10+</div>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Distributor partners</p>
            </div>
            <div>
              <div className="font-display text-3xl md:text-4xl text-[#4a7b9d]">100%</div>
              <p className="mt-1 text-xs uppercase tracking-wider text-slate-500">Building materials focus</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="bg-[#eef3f7] py-20">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#4a6c7b] rounded-3xl p-10 md:p-14 text-center shadow-xl">
          <ShieldCheck size={40} className="mx-auto text-white/80" />
          <h2 className="font-display text-white text-3xl md:text-4xl lg:text-5xl mt-5 leading-tight">
            Curious whether we're a fit?
          </h2>
          <p className="mt-4 text-white/80 max-w-xl mx-auto">
            This isn't a sales site. It's an introduction. If our story sounds like the partner you've been looking for, we'd love to swap notes.
          </p>
          <Link to="/contact" className="inline-block mt-8">
            <Button className="bg-white text-[#3d5a68] hover:bg-white/90 h-12 px-8 rounded-md font-semibold shadow-lg">
              Drop us a line
              <ArrowRight size={18} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useDocumentMeta({
    title: "Outsourced Building Materials Sales Reps | Creative Sales Source",
    description:
      "Creative Sales Source LLC is the Midwest's trusted outsourced sales force for building-materials manufacturers — connecting your products to distributors and dealers across 9 states since 2012.",
    path: "/",
  });
  useJsonLd([organizationSchema(), localBusinessSchema(), websiteSchema()]);
  return (
    <>
      <Hero />
      <Advantage />
      <Partners />
      <Coverage />
      <Testimonials />
      <CTA />
    </>
  );
}
